import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import type { ConnectSectionId } from './data/connectSections';
import './styles/globals.css';
import './styles/culture-global-theme.css';

import { ErrorBoundary } from './components/ErrorBoundary';
import { CartProvider } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ReferralCapture } from './components/auth/ReferralCapture';
import { FeedProvider } from './context/FeedContext';
import { HideFooterProvider } from './context/HideFooterContext';
import { PlatformLayout } from './components/layout/PlatformLayout';
import { preloadChunkForPath } from './lib/routeChunkPreloads';
import { trackGoogleAdsPageView } from './lib/googleAds';
import { toSafeAlertString } from './lib/safeAlertString';
import { isEmployeeWorkTab } from './lib/operationsStaffTabs';

// Retry wrapper for lazy imports — re-runs the import promise; race with timeout so Suspense cannot hang forever
function lazyRetry(factory: () => Promise<any>, maxRetries = 1): React.LazyExoticComponent<any> {
  return lazy(() => {
    let attemptsLeft = maxRetries + 1;
    const CHUNK_MS = 20000;

    const run = (): Promise<any> => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('Import timed out')), CHUNK_MS);
      });
      return Promise.race([factory(), timeoutPromise])
        .finally(() => {
          if (timer) clearTimeout(timer);
        })
        .catch((err: any) => {
          attemptsLeft -= 1;
          if (attemptsLeft > 0) {
            return new Promise((resolve) => setTimeout(resolve, 400)).then(() => run());
          }
          const reloaded = sessionStorage.getItem('chunk_reload');
          if (!reloaded) {
            sessionStorage.setItem('chunk_reload', '1');
            window.location.reload();
          }
          throw err instanceof Error
            ? err
            : new Error(
                toSafeAlertString(err, 'Failed to load this page. Refresh the tab or clear cache and try again.')
              );
        });
    };
    return run();
  });
}

// Helper for modules with named exports
function lazyNamed(factory: () => Promise<any>): React.LazyExoticComponent<any> {
  return lazyRetry(() => factory().then((m: any) => ({ default: m.default || Object.values(m)[0] })));
}

// Everything is lazy-loaded (no eager imports for pages)
const MainSiteUnlockPage = lazyNamed(() => import('./pages/MainSiteUnlockPage'));
const UnlockLegacyRedirect = lazyNamed(() => import('./pages/UnlockLegacyRedirect'));
const DistrictHome = lazyNamed(() => import('./pages/DistrictHome'));
const LoginPage = lazyNamed(() => import('./pages/LoginPage'));
const RegisterPage = lazyNamed(() => import('./pages/RegisterPage'));

const Market = lazyNamed(() => import('./pages/Market'));
const MakeOfferPage = lazyNamed(() => import('./pages/MakeOffer'));
const ProductDetail = lazyNamed(() => import('./pages/ProductDetail'));
const MarketBrandPage = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.LegacyBrandRedirect })));
const MarketCategoryPage = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.LegacyCategoryRedirect })));
const BrandHubPage = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.BrandHubPage })));
const ModelHubPage = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.ModelHubPage })));
const ColorwayHubPage = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.ColorwayHubPage })));
const CategoryHubPage = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.CategoryHubPage })));
const CityHubPage = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.CityHubPage })));
const CityBuyHubPage = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.CityBuyHubPage })));
const ReleasesIndexPage = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.ReleasesIndexPage })));
const ReleaseMonthPage = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.ReleaseMonthPage })));
const DatabaseHubPage = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.DatabaseHubPage })));
const PriceGuidePage = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.PriceGuidePage })));
const UserProfileRedirect = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.UserProfileRedirect })));
const CitySneakersCatchRoute = lazyNamed(() => import('./pages/seo/SeoEmpirePages').then((m) => ({ default: m.CitySneakersCatchRoute })));
const SellerListingsSeoPage = lazyNamed(() => import('./pages/seo/MarketSeoPages').then((m) => ({ default: m.SellerListingsSeoPage })));
const SellerReviewsSeoPage = lazyNamed(() => import('./pages/seo/MarketSeoPages').then((m) => ({ default: m.SellerReviewsSeoPage })));
const SellerDashboard = lazyNamed(() => import('./pages/SellerDashboard'));
const Culture = lazyNamed(() => import('./pages/CulturePage'));
const CultureHubExplorePage = lazyNamed(() => import('./pages/CultureHubExplorePage'));
const LearnPage = lazyNamed(() => import('./pages/LearnPage'));
const ArticlePage = lazyNamed(() => import('./pages/ArticlePage'));
const CommunityPage = lazyRetry(() => import('./pages/CommunityPage'));
const ConnectPage = lazyNamed(() => import('./pages/ConnectPage'));
const PublicProfileRoute = lazyNamed(() => import('./components/profile/PublicProfileRoute'));
const ProfileNew = lazyNamed(() => import('./pages/ProfileNew'));
const DistrictFeed = lazyRetry(() => import('./pages/DistrictFeed'));
const ChatSystem = lazyRetry(() => import('./pages/ChatSystem'));
const ForgotPasswordPage = lazyNamed(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyNamed(() => import('./pages/ResetPasswordPage'));
const AuthCallbackPage = lazyNamed(() => import('./pages/AuthCallbackPage'));
const TermsPage = lazyNamed(() => import('./pages/TermsPage'));
const PrivacyPage = lazyNamed(() => import('./pages/PrivacyPage'));
const CartPage = lazyRetry(() => import('./pages/CartPage'));
const CheckoutPage = lazyRetry(() => import('./pages/CheckoutPage'));
const CheckoutSuccess = lazyNamed(() => import('./pages/CheckoutSuccess'));
const CreateListing = lazyRetry(() => import('./pages/CreateListing'));
const SellerOnboardingPage = lazyRetry(() => import('./pages/SellerOnboardingPage'));
const DistrictEntryPage = lazyRetry(() => import('./pages/DistrictEntryPage'));
const SellShipPage = lazyRetry(() => import('./pages/sell/SellShipPage'));
const SellOffersPage = lazyRetry(() => import('./pages/sell/SellOffersPage'));
const AcceptTeamInvitePage = lazyNamed(() => import('./pages/sell/AcceptTeamInvitePage'));
const StorefrontPublicPage = lazyNamed(() => import('./pages/store/StorefrontPublicPage'));
const StorefrontSubPage = lazyNamed(() => import('./pages/store/StorefrontSubPage'));
const StoreCollectionPage = lazyNamed(() => import('./pages/store/StoreCollectionPage'));
const StorefrontDirectoryPage = lazyNamed(() => import('./pages/store/StorefrontDirectoryPage'));
const CreatePost = lazyRetry(() => import('./pages/CreatePost'));
const PostPage = lazyRetry(() => import('./pages/PostPage'));
const NewDrops = lazyRetry(() => import('./pages/NewDrops'));
const DropDetail = lazyRetry(() => import('./pages/DropDetail'));
const AdminImportProduct = lazyRetry(() => import('./pages/AdminImportProduct'));
const DistrictClips = lazyNamed(() => import('./pages/DistrictClips'));
const NotificationsPage = lazyNamed(() => import('./pages/NotificationsPage'));
const GamificationHub = lazyNamed(() => import('./pages/GamificationHub'));
const CollectorHQ = lazyNamed(() => import('./pages/CollectorHQ'));
const GrailHuntsPage = lazyNamed(() => import('./pages/GrailHuntsPage'));
const WatchPartyPage = lazyNamed(() => import('./pages/WatchPartyPage'));
const AdminDashboard = lazyNamed(() => import('./pages/AdminDashboard'));
const NotificationAlertDetailPage = lazyNamed(() => import('./pages/NotificationAlertDetailPage'));
const SecretAdminGate = lazyNamed(() => import('./pages/SecretAdminGate'));
const AboutDistrict = lazyNamed(() => import('./pages/AboutDistrict'));
const ExplorePage = lazyRetry(() => import('./pages/ExplorePage'));
const OrderHistory = lazyRetry(() => import('./pages/OrderHistory'));
const SettingsPage = lazyRetry(() => import('./pages/SettingsPage'));
const RefundPage = lazyRetry(() => import('./pages/RefundPage'));
const AuthenticationPolicyPage = lazyRetry(() => import('./pages/AuthenticationPolicyPage'));
const CreatorAgreementPage = lazyRetry(() => import('./pages/CreatorAgreementPage'));
const CommunityGuidelinesPage = lazyRetry(() => import('./pages/CommunityGuidelinesPage'));
const AcceptableUsePage = lazyRetry(() => import('./pages/AcceptableUsePage'));
const FeeDisclosurePage = lazyRetry(() => import('./pages/FeeDisclosurePage'));
const ShippingPolicyPage = lazyRetry(() => import('./pages/ShippingPolicyPage'));
const DisputeResolutionPage = lazyRetry(() => import('./pages/DisputeResolutionPage'));
const SellerAgreementPage = lazyRetry(() => import('./pages/SellerAgreementPage'));
const CounterfeitPolicyPage = lazyRetry(() => import('./pages/CounterfeitPolicyPage'));
const ChargebackPolicyPage = lazyRetry(() => import('./pages/ChargebackPolicyPage'));
const IdentityVerificationPage = lazyRetry(() => import('./pages/IdentityVerificationPage'));
const SellerPerformancePage = lazyRetry(() => import('./pages/SellerPerformancePage'));
const TrustSafetyPage = lazyRetry(() => import('./pages/TrustSafetyPage'));
const RiskReservePage = lazyRetry(() => import('./pages/RiskReservePage'));
const ModerationPolicyPage = lazyRetry(() => import('./pages/ModerationPolicyPage'));
const GovernanceCharterPage = lazyRetry(() => import('./pages/GovernanceCharterPage'));
const LiabilityDisclaimerPage = lazyRetry(() => import('./pages/LiabilityDisclaimerPage'));
const WalletTermsPage = lazyRetry(() => import('./pages/WalletTermsPage'));
const DataRetentionPage = lazyRetry(() => import('./pages/DataRetentionPage'));
const AIModerationPage = lazyRetry(() => import('./pages/AIModerationPage'));
const TaxReportingPage = lazyRetry(() => import('./pages/TaxReportingPage'));
const SecurityPolicyPage = lazyRetry(() => import('./pages/SecurityPolicyPage'));
const AdvertisingPolicyPage = lazyRetry(() => import('./pages/AdvertisingPolicyPage'));
const EmergencyResponsePage = lazyRetry(() => import('./pages/EmergencyResponsePage'));
const HowOrdersWorkPage = lazyRetry(() => import('./pages/HowOrdersWorkPage'));
const HowItWorksPage = lazyRetry(() => import('./pages/HowItWorksPage'));
const BuyingGuidePage = lazyRetry(() => import('./pages/BuyingGuidePage'));
const SellingGuidePage = lazyRetry(() => import('./pages/SellingGuidePage'));
const VerificationProcessPage = lazyRetry(() => import('./pages/VerificationProcessPage'));
const WhyTrustPage = lazyRetry(() => import('./pages/WhyTrustPage'));
const FAQPage = lazyRetry(() => import('./pages/FAQPage'));
const SomethingWrongPage = lazyRetry(() => import('./pages/SomethingWrongPage'));
const TrustCenterPage = lazyRetry(() => import('./pages/TrustCenterPage'));
const BuyerProtectionPage = lazyRetry(() => import('./pages/BuyerProtectionPage'));
const SellerProtectionPage = lazyRetry(() => import('./pages/SellerProtectionPage'));
const SecurePaymentsPage = lazyRetry(() => import('./pages/SecurePaymentsPage'));
const TransparencyPage = lazyRetry(() => import('./pages/TransparencyPage'));
const SupportCenterPage = lazyRetry(() => import('./pages/SupportCenterPage'));
const VerificationStoryPage = lazyRetry(() => import('./pages/VerificationStoryPage'));
const FounderLetterPage = lazyRetry(() => import('./pages/FounderLetterPage'));
const SafetyManifestoPage = lazyRetry(() => import('./pages/SafetyManifestoPage'));
const OrderTrackingPage = lazyRetry(() => import('./pages/OrderTrackingPage'));
const GrowthHubPage = lazyRetry(() => import('./pages/GrowthHubPage'));
const ReferralProgram = lazyNamed(() => import('./pages/ReferralProgram'));
const CreatorRevenuePage = lazyRetry(() => import('./pages/CreatorRevenuePage'));
const AffiliatePage = lazyRetry(() => import('./pages/AffiliatePage'));
const DropPartnershipsPage = lazyRetry(() => import('./pages/DropPartnershipsPage'));
const CampusAmbassadorPage = lazyRetry(() => import('./pages/CampusAmbassadorPage'));
const AmbassadorCommandCenter = lazyNamed(() => import('./pages/ambassador/AmbassadorCommandCenter'));
const NexusHeadquarters = lazyNamed(() => import('./pages/nexus/NexusHeadquarters'));
const OperatorPortfolioPage = lazyNamed(() => import('./pages/nexus/OperatorPortfolioPage'));
const BrandCollabsPage = lazyRetry(() => import('./pages/BrandCollabsPage'));
const CreatorStorefrontsPage = lazyRetry(() => import('./pages/CreatorStorefrontsPage'));
const InfluencerProgramPage = lazyRetry(() => import('./pages/InfluencerProgramPage'));
const ShareRewardsPage = lazyRetry(() => import('./pages/ShareRewardsPage'));
const BetaAccessPage = lazyRetry(() => import('./pages/BetaAccessPage'));
const CareersPage = lazyNamed(() => import('./pages/CareersPage'));

// Emotional Hooks
const EmotionalHubPage = lazyRetry(() => import('./pages/EmotionalHubPage'));
const DreamGrailsPage = lazyRetry(() => import('./pages/DreamGrailsPage'));
const StyleQuizPage = lazyRetry(() => import('./pages/StyleQuizPage'));
const CollectionGalleryPage = lazyRetry(() => import('./pages/CollectionGalleryPage'));
const MoodBoardsPage = lazyRetry(() => import('./pages/MoodBoardsPage'));
const MemoryPostsPage = lazyRetry(() => import('./pages/MemoryPostsPage'));
const JourneyTimelinePage = lazyRetry(() => import('./pages/JourneyTimelinePage'));
const MilestonesPage = lazyRetry(() => import('./pages/MilestonesPage'));

// Intelligence / Power Features (hub landing archived — see pages/IntelligenceHubPage.tsx; restore lazy + /intelligence route when shipping)
const SmartPricingPage = lazyRetry(() => import('./pages/SmartPricingPage'));
const ListingInsightsPage = lazyRetry(() => import('./pages/ListingInsightsPage'));
const DemandMapPage = lazyRetry(() => import('./pages/DemandMapPage'));
const MarketTrendsPage = lazyRetry(() => import('./pages/MarketTrendsPage'));
const AutoTagsPage = lazyRetry(() => import('./pages/AutoTagsPage'));
const FraudDetectionPage = lazyRetry(() => import('./pages/FraudDetectionPage'));
const SmartNotificationsPage = lazyRetry(() => import('./pages/SmartNotificationsPage'));
const DynamicFeedPage = lazyRetry(() => import('./pages/DynamicFeedPage'));
const DiscoveryEnginePage = lazyRetry(() => import('./pages/DiscoveryEnginePage'));

// Existing unrouted pages
const AuctionHouse = lazyRetry(() => import('./pages/AuctionHouse'));
const ShippingCalculator = lazyRetry(() => import('./pages/ShippingCalculator'));
const VirtualTryOn = lazyRetry(() => import('./pages/VirtualTryOn'));

// New feature pages
const FlashSalesPage = lazyRetry(() => import('./pages/FlashSalesPage'));
const SalesForecastPage = lazyRetry(() => import('./pages/SalesForecastPage'));
const CompetitorMonitorPage = lazyRetry(() => import('./pages/CompetitorMonitorPage'));
const MarketingHubPage = lazyRetry(() => import('./pages/MarketingHubPage'));
const ItemQAPage = lazyRetry(() => import('./pages/ItemQAPage'));
const GeoListingsPage = lazyRetry(() => import('./pages/GeoListingsPage'));
const FeatureTogglesPage = lazyRetry(() => import('./pages/FeatureTogglesPage'));

// Power Pages
const VaultPage = lazyRetry(() => import('./pages/VaultPage'));
const WarehouseWmsPage = lazyNamed(() => import('./pages/WarehouseWmsPage'));

/** Preload lazy chunk for the active route on every navigation (webpackPrefetch hints + warm cache). */
function RoutePreloader() {
  const { pathname } = useLocation();
  useEffect(() => {
    preloadChunkForPath(pathname);
  }, [pathname]);
  return null;
}

function RedirectAdminToOperations() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/operations', search }} replace />;
}

function RedirectEmployeeToOperations() {
  const [sp] = useSearchParams();
  const t = sp.get('tab');
  if (t === 'learn') {
    return <Navigate to="/operations?tab=learn-studio&learnHub=guides" replace />;
  }
  const tab = t && isEmployeeWorkTab(t) ? `staff-${t}` : 'staff-import';
  return <Navigate to={`/operations?tab=${tab}`} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/** Report SPA navigations to Google Ads / GA4 (initial load is handled in index.html). */
function GoogleAdsRouteTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    trackGoogleAdsPageView(`${pathname}${search}`);
  }, [pathname, search]);
  return null;
}

/** Preserve query params (e.g. ?clip=) when moving legacy URLs to Connect */
const LegacyConnectRedirect: React.FC<{ section: ConnectSectionId }> = ({ section }) => {
  const [searchParams] = useSearchParams();
  const next = new URLSearchParams(searchParams);
  next.set('section', section);
  return <Navigate to={`/connect?${next.toString()}`} replace />;
};

const PageLoader: React.FC = () => {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto" />
        {slow && (
          <div className="mt-6">
            <p className="text-gray-500 text-sm mb-3">Taking longer than expected...</p>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-full transition-colors"
            >
              Reload
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <ReferralCapture />
      <HideFooterProvider>
      <RoutePreloader />
      <ScrollToTop />
      <GoogleAdsRouteTracker />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Home = private access lock screen when site lock is enabled */}
          <Route path="/" element={<MainSiteUnlockPage />} />
          <Route path="/unlock" element={<UnlockLegacyRedirect />} />

          <Route path="/district" element={<PlatformLayout><DistrictHome /></PlatformLayout>} />
          <Route path="/clips" element={<LegacyConnectRedirect section="clips" />} />
          <Route path="/connect" element={<PlatformLayout><ConnectPage /></PlatformLayout>} />
          <Route path="/about" element={<PlatformLayout><AboutDistrict /></PlatformLayout>} />
          <Route path="/explore" element={<PlatformLayout><ExplorePage /></PlatformLayout>} />
          {/* Removed: /features */}

          {/* Market */}
          <Route path="/market" element={<PlatformLayout><Market /></PlatformLayout>} />
          <Route path="/market/brand/:brandSlug" element={<PlatformLayout><MarketBrandPage /></PlatformLayout>} />
          <Route path="/market/category/:categorySlug" element={<PlatformLayout><MarketCategoryPage /></PlatformLayout>} />
          <Route path="/market/offers" element={<PlatformLayout><MakeOfferPage /></PlatformLayout>} />
          <Route path="/market/product/:id" element={<PlatformLayout><ProductDetail /></PlatformLayout>} />

          {/* Programmatic SEO */}
          <Route path="/products/:productSlug" element={<PlatformLayout><ProductDetail /></PlatformLayout>} />
          <Route path="/brands/:brandSlug" element={<PlatformLayout><BrandHubPage /></PlatformLayout>} />
          <Route path="/models/:modelSlug" element={<PlatformLayout><ModelHubPage /></PlatformLayout>} />
          <Route path="/colorways/:colorwaySlug" element={<PlatformLayout><ColorwayHubPage /></PlatformLayout>} />
          <Route path="/categories/:categorySlug" element={<PlatformLayout><CategoryHubPage /></PlatformLayout>} />
          <Route path="/cities/:citySlug" element={<PlatformLayout><CityHubPage /></PlatformLayout>} />
          <Route path="/buy/:citySlug/:productSlug" element={<PlatformLayout><CityBuyHubPage /></PlatformLayout>} />
          <Route path="/releases" element={<PlatformLayout><ReleasesIndexPage /></PlatformLayout>} />
          <Route path="/releases/:releaseSlug" element={<PlatformLayout><ReleaseMonthPage /></PlatformLayout>} />
          <Route path="/database" element={<PlatformLayout><DatabaseHubPage /></PlatformLayout>} />
          <Route path="/price-guide/:slug" element={<PlatformLayout><PriceGuidePage /></PlatformLayout>} />
          <Route path="/user/:username" element={<PlatformLayout><UserProfileRedirect /></PlatformLayout>} />
          <Route path="/calendar" element={<Navigate to="/releases" replace />} />
          <Route path="/authentication" element={<Navigate to="/verification-process" replace />} />
          <Route path="/streetwear" element={<Navigate to="/categories/streetwear" replace />} />

          {/* Sell */}
          <Route path="/sell" element={<PlatformLayout><SellerDashboard /></PlatformLayout>} />
          <Route path="/entry" element={<DistrictEntryPage />} />
          <Route path="/sell/onboard" element={<SellerOnboardingPage />} />
          <Route path="/sell/dashboard" element={<PlatformLayout><SellerDashboard /></PlatformLayout>} />
          <Route path="/sell/new" element={<PlatformLayout><CreateListing /></PlatformLayout>} />
          <Route path="/sell/ship" element={<PlatformLayout><SellShipPage /></PlatformLayout>} />
          <Route path="/sell/offers" element={<PlatformLayout><SellOffersPage /></PlatformLayout>} />
          <Route path="/sell/storefront/team/accept" element={<PlatformLayout><AcceptTeamInvitePage /></PlatformLayout>} />

          {/* Storefronts */}
          <Route path="/stores" element={<PlatformLayout><StorefrontDirectoryPage /></PlatformLayout>} />
          <Route path="/store/:slug/collections/:collectionSlug" element={<PlatformLayout><StoreCollectionPage /></PlatformLayout>} />
          <Route path="/store/:slug/:pageKey" element={<PlatformLayout><StorefrontSubPage /></PlatformLayout>} />
          <Route path="/store/:slug" element={<PlatformLayout><StorefrontPublicPage /></PlatformLayout>} />

          {/* Drops */}
          <Route path="/drops" element={<PlatformLayout><NewDrops /></PlatformLayout>} />
          <Route path="/drops/:id" element={<PlatformLayout><DropDetail /></PlatformLayout>} />
          <Route path="/admin/import" element={<PlatformLayout><AdminImportProduct /></PlatformLayout>} />

          {/* Culture & Learn */}
          <Route path="/culture/explore/:hubSlug" element={<PlatformLayout><CultureHubExplorePage /></PlatformLayout>} />
          <Route path="/culture" element={<PlatformLayout><Culture /></PlatformLayout>} />
          <Route path="/learn" element={<PlatformLayout><LearnPage /></PlatformLayout>} />
          <Route path="/learn/:slug" element={<PlatformLayout><ArticlePage /></PlatformLayout>} />

          {/* Community & Social */}
          <Route path="/community" element={<LegacyConnectRedirect section="universe" />} />
          <Route path="/feed" element={<PlatformLayout><DistrictFeed /></PlatformLayout>} />
          {/* Composer lives inside Connect tabs now */}
          <Route path="/post/new" element={<Navigate to="/connect?section=posts" replace />} />
          <Route path="/post/:postId" element={<PlatformLayout><PostPage /></PlatformLayout>} />
          <Route path="/messages/:conversationId" element={<PlatformLayout><ChatSystem /></PlatformLayout>} />
          <Route path="/messages" element={<PlatformLayout><ChatSystem /></PlatformLayout>} />
          {/* Public profiles: /{role}/{username} */}
          <Route path="/creator/:username/listings" element={<PlatformLayout><SellerListingsSeoPage /></PlatformLayout>} />
          <Route path="/creator/:username/reviews" element={<PlatformLayout><SellerReviewsSeoPage /></PlatformLayout>} />
          <Route path="/collector/:username/listings" element={<PlatformLayout><SellerListingsSeoPage /></PlatformLayout>} />
          <Route path="/collector/:username/reviews" element={<PlatformLayout><SellerReviewsSeoPage /></PlatformLayout>} />
          <Route path="/seller/:username/listings" element={<PlatformLayout><SellerListingsSeoPage /></PlatformLayout>} />
          <Route path="/seller/:username/reviews" element={<PlatformLayout><SellerReviewsSeoPage /></PlatformLayout>} />
          <Route path="/curator/:username/listings" element={<PlatformLayout><SellerListingsSeoPage /></PlatformLayout>} />
          <Route path="/curator/:username/reviews" element={<PlatformLayout><SellerReviewsSeoPage /></PlatformLayout>} />
          <Route path="/creator/:username" element={<PlatformLayout><PublicProfileRoute /></PlatformLayout>} />
          <Route path="/collector/:username" element={<PlatformLayout><PublicProfileRoute /></PlatformLayout>} />
          <Route path="/seller/:username" element={<PlatformLayout><PublicProfileRoute /></PlatformLayout>} />
          <Route path="/curator/:username" element={<PlatformLayout><PublicProfileRoute /></PlatformLayout>} />
          <Route path="/creators" element={<Navigate to="/culture#featured" replace />} />

          {/* User */}
          <Route path="/cart" element={<PlatformLayout><CartPage /></PlatformLayout>} />
          <Route path="/checkout" element={<PlatformLayout><CheckoutPage /></PlatformLayout>} />
          <Route path="/checkout/success" element={<PlatformLayout><CheckoutSuccess /></PlatformLayout>} />
          <Route path="/profile" element={<PlatformLayout><ProfileNew /></PlatformLayout>} />
          <Route path="/settings" element={<PlatformLayout><SettingsPage /></PlatformLayout>} />
          <Route path="/orders" element={<PlatformLayout><OrderHistory /></PlatformLayout>} />
          <Route path="/orders/:orderId" element={<PlatformLayout><OrderTrackingPage /></PlatformLayout>} />
          <Route path="/notifications" element={<PlatformLayout><NotificationsPage /></PlatformLayout>} />
          <Route path="/progress" element={<PlatformLayout><GamificationHub /></PlatformLayout>} />
          <Route path="/collector" element={<PlatformLayout><CollectorHQ /></PlatformLayout>} />
          <Route path="/grail-hunts" element={<PlatformLayout><GrailHuntsPage /></PlatformLayout>} />
          <Route path="/watch-parties" element={<PlatformLayout><WatchPartyPage /></PlatformLayout>} />
          <Route path="/watch-party/:partyId" element={<PlatformLayout><WatchPartyPage /></PlatformLayout>} />
          <Route
            path="/operations/notifications/:notificationId"
            element={
              <PlatformLayout>
                <NotificationAlertDetailPage />
              </PlatformLayout>
            }
          />
          <Route path="/operations" element={<PlatformLayout><AdminDashboard /></PlatformLayout>} />
          <Route path="/admin" element={<RedirectAdminToOperations />} />
          <Route path="/employee" element={<RedirectEmployeeToOperations />} />
          <Route path="/warehouse" element={<PlatformLayout><WarehouseWmsPage /></PlatformLayout>} />
          {/* Secret admin — /district88/ops, password-protected, no nav */}
          <Route path="/district88/ops" element={<SecretAdminGate />} />

          {/* Auth */}
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/login" element={<PlatformLayout><LoginPage /></PlatformLayout>} />
          <Route path="/register" element={<PlatformLayout><RegisterPage /></PlatformLayout>} />
          <Route path="/forgot-password" element={<PlatformLayout><ForgotPasswordPage /></PlatformLayout>} />

          {/* Legal */}
          <Route path="/terms" element={<PlatformLayout><TermsPage /></PlatformLayout>} />
          <Route path="/privacy" element={<PlatformLayout><PrivacyPage /></PlatformLayout>} />
          <Route path="/refunds" element={<PlatformLayout><RefundPage /></PlatformLayout>} />
          <Route path="/careers" element={<PlatformLayout><CareersPage /></PlatformLayout>} />
          <Route path="/authentication-policy" element={<PlatformLayout><AuthenticationPolicyPage /></PlatformLayout>} />
          <Route path="/creator-agreement" element={<PlatformLayout><CreatorAgreementPage /></PlatformLayout>} />
          <Route path="/community-guidelines" element={<PlatformLayout><CommunityGuidelinesPage /></PlatformLayout>} />
          <Route path="/acceptable-use" element={<PlatformLayout><AcceptableUsePage /></PlatformLayout>} />
          <Route path="/fees" element={<PlatformLayout><FeeDisclosurePage /></PlatformLayout>} />
          <Route path="/shipping-policy" element={<PlatformLayout><ShippingPolicyPage /></PlatformLayout>} />
          <Route path="/dispute-resolution" element={<PlatformLayout><DisputeResolutionPage /></PlatformLayout>} />
          <Route path="/seller-agreement" element={<PlatformLayout><SellerAgreementPage /></PlatformLayout>} />
          <Route path="/counterfeit-policy" element={<PlatformLayout><CounterfeitPolicyPage /></PlatformLayout>} />
          <Route path="/chargeback-policy" element={<PlatformLayout><ChargebackPolicyPage /></PlatformLayout>} />
          <Route path="/identity-verification" element={<PlatformLayout><IdentityVerificationPage /></PlatformLayout>} />
          <Route path="/seller-performance" element={<PlatformLayout><SellerPerformancePage /></PlatformLayout>} />
          <Route path="/trust-safety" element={<PlatformLayout><TrustSafetyPage /></PlatformLayout>} />
          <Route path="/risk-reserve" element={<PlatformLayout><RiskReservePage /></PlatformLayout>} />
          <Route path="/moderation-policy" element={<PlatformLayout><ModerationPolicyPage /></PlatformLayout>} />
          <Route path="/governance" element={<PlatformLayout><GovernanceCharterPage /></PlatformLayout>} />
          <Route path="/liability-disclaimer" element={<PlatformLayout><LiabilityDisclaimerPage /></PlatformLayout>} />
          <Route path="/wallet-terms" element={<PlatformLayout><WalletTermsPage /></PlatformLayout>} />
          <Route path="/data-retention" element={<PlatformLayout><DataRetentionPage /></PlatformLayout>} />
          <Route path="/ai-moderation" element={<PlatformLayout><AIModerationPage /></PlatformLayout>} />
          <Route path="/tax-reporting" element={<PlatformLayout><TaxReportingPage /></PlatformLayout>} />
          <Route path="/security-policy" element={<PlatformLayout><SecurityPolicyPage /></PlatformLayout>} />
          <Route path="/advertising-policy" element={<PlatformLayout><AdvertisingPolicyPage /></PlatformLayout>} />
          <Route path="/emergency-response" element={<PlatformLayout><EmergencyResponsePage /></PlatformLayout>} />
          <Route path="/how-orders-work" element={<PlatformLayout><HowOrdersWorkPage /></PlatformLayout>} />
          <Route path="/how-it-works" element={<PlatformLayout><HowItWorksPage /></PlatformLayout>} />
          <Route path="/buying-guide" element={<PlatformLayout><BuyingGuidePage /></PlatformLayout>} />
          <Route path="/selling-guide" element={<PlatformLayout><SellingGuidePage /></PlatformLayout>} />
          <Route path="/verification-process" element={<PlatformLayout><VerificationProcessPage /></PlatformLayout>} />
          <Route path="/why-trust" element={<PlatformLayout><WhyTrustPage /></PlatformLayout>} />
          <Route path="/faq" element={<PlatformLayout><FAQPage /></PlatformLayout>} />
          <Route path="/something-wrong" element={<PlatformLayout><SomethingWrongPage /></PlatformLayout>} />

          {/* Trust Center */}
          <Route path="/trust" element={<PlatformLayout><TrustCenterPage /></PlatformLayout>} />
          <Route path="/buyer-protection" element={<PlatformLayout><BuyerProtectionPage /></PlatformLayout>} />
          <Route path="/seller-protection" element={<PlatformLayout><SellerProtectionPage /></PlatformLayout>} />
          <Route path="/secure-payments" element={<PlatformLayout><SecurePaymentsPage /></PlatformLayout>} />
          <Route path="/transparency" element={<PlatformLayout><TransparencyPage /></PlatformLayout>} />
          <Route path="/support" element={<PlatformLayout><SupportCenterPage /></PlatformLayout>} />
          <Route path="/verification-story" element={<PlatformLayout><VerificationStoryPage /></PlatformLayout>} />
          <Route path="/founder-letter" element={<PlatformLayout><FounderLetterPage /></PlatformLayout>} />
          <Route path="/safety-manifesto" element={<PlatformLayout><SafetyManifestoPage /></PlatformLayout>} />
          {/* Growth Engine */}
          <Route path="/grow" element={<PlatformLayout><GrowthHubPage /></PlatformLayout>} />
          <Route path="/referral" element={<PlatformLayout><ReferralProgram /></PlatformLayout>} />
          <Route path="/creator-revenue" element={<PlatformLayout><CreatorRevenuePage /></PlatformLayout>} />
          <Route path="/affiliates" element={<PlatformLayout><AffiliatePage /></PlatformLayout>} />
          <Route path="/drop-partnerships" element={<PlatformLayout><DropPartnershipsPage /></PlatformLayout>} />
          <Route path="/nexus" element={<NexusHeadquarters />} />
          <Route path="/operator/:username" element={<PlatformLayout><OperatorPortfolioPage /></PlatformLayout>} />
          <Route path="/ambassador" element={<Navigate to="/nexus" replace />} />
          <Route path="/ambassadors" element={<PlatformLayout><CampusAmbassadorPage /></PlatformLayout>} />
          <Route path="/brand-collabs" element={<PlatformLayout><BrandCollabsPage /></PlatformLayout>} />
          <Route path="/creator-storefronts" element={<PlatformLayout><CreatorStorefrontsPage /></PlatformLayout>} />
          <Route path="/influencer-program" element={<PlatformLayout><InfluencerProgramPage /></PlatformLayout>} />
          <Route path="/share-rewards" element={<PlatformLayout><ShareRewardsPage /></PlatformLayout>} />
          <Route path="/beta-access" element={<PlatformLayout><BetaAccessPage /></PlatformLayout>} />

          {/* Emotional Hooks */}
          <Route path="/heart" element={<PlatformLayout><EmotionalHubPage /></PlatformLayout>} />
          <Route path="/dream-grails" element={<PlatformLayout><DreamGrailsPage /></PlatformLayout>} />
          <Route path="/style-quiz" element={<PlatformLayout><StyleQuizPage /></PlatformLayout>} />
          <Route path="/collection-gallery" element={<PlatformLayout><CollectionGalleryPage /></PlatformLayout>} />
          <Route path="/mood-boards" element={<PlatformLayout><MoodBoardsPage /></PlatformLayout>} />
          <Route path="/memory-posts" element={<PlatformLayout><MemoryPostsPage /></PlatformLayout>} />
          <Route path="/journey" element={<PlatformLayout><JourneyTimelinePage /></PlatformLayout>} />
          <Route path="/milestones" element={<PlatformLayout><MilestonesPage /></PlatformLayout>} />

          {/* Intelligence / Power Features */}
          {/* Removed: /ai-stylist */}
          <Route path="/smart-pricing" element={<PlatformLayout><SmartPricingPage /></PlatformLayout>} />
          <Route path="/listing-insights" element={<PlatformLayout><ListingInsightsPage /></PlatformLayout>} />
          <Route path="/demand-map" element={<PlatformLayout><DemandMapPage /></PlatformLayout>} />
          <Route path="/market-trends" element={<PlatformLayout><MarketTrendsPage /></PlatformLayout>} />
          <Route path="/auto-tags" element={<PlatformLayout><AutoTagsPage /></PlatformLayout>} />
          <Route path="/fraud-detection" element={<PlatformLayout><FraudDetectionPage /></PlatformLayout>} />
          <Route path="/smart-notifications" element={<PlatformLayout><SmartNotificationsPage /></PlatformLayout>} />
          <Route path="/dynamic-feed" element={<PlatformLayout><DynamicFeedPage /></PlatformLayout>} />
          <Route path="/discovery" element={<PlatformLayout><DiscoveryEnginePage /></PlatformLayout>} />

          {/* Marketplace Features */}
          <Route path="/auctions" element={<PlatformLayout><AuctionHouse /></PlatformLayout>} />
          <Route path="/shipping-calculator" element={<PlatformLayout><ShippingCalculator /></PlatformLayout>} />
          <Route path="/virtual-tryon" element={<PlatformLayout><VirtualTryOn /></PlatformLayout>} />
          <Route path="/flash-sales" element={<PlatformLayout><FlashSalesPage /></PlatformLayout>} />
          <Route path="/item-qa" element={<PlatformLayout><ItemQAPage /></PlatformLayout>} />
          <Route path="/near-me" element={<PlatformLayout><GeoListingsPage /></PlatformLayout>} />

          {/* Seller Tools */}
          <Route path="/sales-forecast" element={<PlatformLayout><SalesForecastPage /></PlatformLayout>} />
          <Route path="/competitor-monitor" element={<PlatformLayout><CompetitorMonitorPage /></PlatformLayout>} />
          <Route path="/marketing-hub" element={<PlatformLayout><MarketingHubPage /></PlatformLayout>} />

          {/* Power Pages */}
          <Route path="/vault" element={<PlatformLayout><VaultPage /></PlatformLayout>} />
          {/* Removed: /drop-radar, /lab */}

          {/* Admin */}
          <Route path="/admin/features" element={<PlatformLayout><FeatureTogglesPage /></PlatformLayout>} />

          <Route path="/fee-disclosure" element={<Navigate to="/fees" replace />} />
          <Route path="/shipping" element={<Navigate to="/shipping-policy" replace />} />

          {/* City SEO catch-all — must stay above 404 */}
          <Route path="/:citySlug" element={<PlatformLayout><CitySneakersCatchRoute /></PlatformLayout>} />

          {/* 404 */}
          <Route
            path="*"
            element={
              <PlatformLayout>
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-6xl font-bold text-district-red mb-4">404</h1>
                    <p className="text-xl text-gray-200 mb-8">This corner of the District doesn't exist yet.</p>
                    <a href="/" className="btn-primary">Back to the movement</a>
                  </div>
                </div>
              </PlatformLayout>
            }
          />
        </Routes>
      </Suspense>
      </HideFooterProvider>
    </Router>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
          <FeedProvider>
            <AppRoutes />
          </FeedProvider>
        </CartProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
